/**
 * PeriodSwitch — the YTD / 3-mo / 12-mo bascule that windows the trend lines. A WAI-ARIA radiogroup
 * with full roving-keyboard support (Arrow/Home/End), token-styled as a segmented control. Keyboard-
 * first: the active segment is the single tab stop; arrows move + select. Mirrors under dir="rtl".
 */
import { useRef, type KeyboardEvent } from "react";
import { useT } from "@/app";
import type { Period } from "../types";

const PERIODS: ReadonlyArray<{ id: Period; labelKey: string }> = [
  { id: "ytd", labelKey: "analytics.period.ytd.short" },
  { id: "3mo", labelKey: "analytics.period.3mo" },
  { id: "12mo", labelKey: "analytics.period.12mo" },
];

export function PeriodSwitch({ value, onChange }: { value: Period; onChange: (p: Period) => void }) {
  const { t } = useT();
  const refs = useRef<Array<HTMLButtonElement | null>>([]);

  const move = (from: number, delta: number) => {
    const next = (from + delta + PERIODS.length) % PERIODS.length;
    const target = PERIODS[next]!;
    onChange(target.id);
    refs.current[next]?.focus();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>, idx: number) => {
    switch (e.key) {
      case "ArrowRight":
      case "ArrowDown":
        e.preventDefault();
        move(idx, 1);
        break;
      case "ArrowLeft":
      case "ArrowUp":
        e.preventDefault();
        move(idx, -1);
        break;
      case "Home":
        e.preventDefault();
        onChange(PERIODS[0]!.id);
        refs.current[0]?.focus();
        break;
      case "End":
        e.preventDefault();
        onChange(PERIODS[PERIODS.length - 1]!.id);
        refs.current[PERIODS.length - 1]?.focus();
        break;
      default:
        break;
    }
  };

  return (
    <div
      role="radiogroup"
      aria-label={t("analytics.period.label")}
      style={{
        display: "inline-flex",
        gap: "var(--nv-s-1)",
        padding: "var(--nv-s-1)",
        borderRadius: "var(--nv-r-pill)",
        background: "var(--nv-surface-2)",
        border: "1px solid var(--nv-line-faint)",
      }}
    >
      {PERIODS.map((p, i) => {
        const selected = p.id === value;
        return (
          <button
            key={p.id}
            ref={(el) => {
              refs.current[i] = el;
            }}
            type="button"
            role="radio"
            aria-checked={selected}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(p.id)}
            onKeyDown={(e) => onKeyDown(e, i)}
            className="nv-focusable"
            style={{
              appearance: "none",
              cursor: "pointer",
              border: "none",
              minHeight: "var(--nv-min-touch)",
              padding: "0 var(--nv-s-3)",
              borderRadius: "var(--nv-r-pill)",
              fontSize: "var(--nv-fs-label)",
              fontWeight: selected ? 600 : 500,
              color: selected ? "var(--nv-white)" : "var(--nv-slate)",
              background: selected ? "var(--nv-azure)" : "transparent",
              transition: "background var(--nv-fast) var(--nv-ease)",
            }}
          >
            {t(p.labelKey)}
          </button>
        );
      })}
    </div>
  );
}
